import { Component, effect, inject, signal, viewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ProduktTypeService } from '@shared/services/produkt-type.service';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Editor, EditorModule } from 'primeng/editor';
import { InputText } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { MultiSelectModule } from 'primeng/multiselect';
import { Select } from 'primeng/select';
import { LetDatePickerComponent } from '../../../shared/let-datepicker/let-date-picker.component';
import { DriftsMeddelelse } from '../driftsmeddelelse.model';
import { DriftsMeddelelserApi } from '../driftsmeddelelser.api';
import { DriftsMeddelelserService } from '../driftsmeddelelser.service';
import { Textarea } from 'primeng/textarea';
import { getDiffAsText } from '@core/form-utils';
import { DriftsMeddelelserHistorikService } from '../driftsmeddelelser-historik.service';
import dayjs from 'dayjs';
import { ScrollPanel } from 'primeng/scrollpanel';
import { TableModule } from 'primeng/table';
import { TableRowDirective } from '@core/primevue/directives/table-row.directive';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-driftmeddelelser-detail',
  imports: [
    ReactiveFormsModule,
    InputText,
    ButtonModule,
    MessageModule,
    ConfirmDialog,
    MultiSelectModule,
    Select,
    EditorModule,
    LetDatePickerComponent,
    Textarea,
    ScrollPanel,
    TableModule,
    TableRowDirective,
    DatePipe,
  ],
  providers: [ConfirmationService],
  templateUrl: './driftmeddelelser-detail.component.html',
  styleUrl: './driftmeddelelser-detail.component.css',
})
export class DriftmeddelelserDetailComponent {
  confirmationService = inject(ConfirmationService);
  produktTypeService = inject(ProduktTypeService);
  driftsMeddelelserService = inject(DriftsMeddelelserService);
  driftsMeddelelseHistorikService = inject(DriftsMeddelelserHistorikService);
  driftsMeddelelserApi = inject(DriftsMeddelelserApi);

  historikData = this.driftsMeddelelseHistorikService.datasource.data;

  editor = viewChild<Editor>('editor');
  editorValue = signal<string | undefined>(undefined);

  fb = inject(FormBuilder);

  form = this.fb.group({
    vistStatus: [0, [Validators.required, Validators.min(0)]],
    emne: ['', [Validators.required]],
    indhold: ['', [Validators.required]],
    kommentar: ['', [Validators.required]],
    start: ['', [Validators.required]],
    slut: ['', [Validators.required]],
    regNr: [''],
    itc: ['', [Validators.required]],
    driftsystem: [[] as string[], [Validators.required]],
    produkter: [[] as number[]],
    redigeringsStatus: [0, [Validators.required, Validators.min(0)]],
  });
  saving = signal(false);

  constructor() {
    effect(() => {
      const m = this.driftsMeddelelserService.selectedDriftsMeddelelse();
      if (m) {
        let produkter: number[] = [];
        if (m.produkter) {
          produkter = m.produkter?.split(',').map(x => parseInt(x));
        }
        const driftsystem = m.driftsystem?.split(',');
        this.form.patchValue({
          ...m,
          produkter: produkter,
          driftsystem: driftsystem,
        });

        //Hack fordi PrimeNg Editor ikke reagerer på form.patchValue
        //https://github.com/primefaces/primeng/issues/16825
        setTimeout(() => {
          const editorField = this.editor();
          editorField?.writeValue(m.indhold);
        }, 100);

        this.driftsMeddelelseHistorikService.datasource.driftsMeddelelseId.set(
          m.id
        );
      } else {
        this.form.patchValue({
          vistStatus: 0,
          redigeringsStatus: 0,
          itc: 'Alle',
          start: dayjs().toISOString(),
          slut: dayjs().endOf('day').toISOString(),
        });
      }
    });
  }

  async onClickSave() {
    await this.saveChanges();
  }

  async onClickDelete(event: Event) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Er du sikker?',
      header: 'Slet driftsmeddelelse',
      rejectButtonProps: {
        label: 'Fortryd',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Slet',
        severity: 'danger',
      },

      accept: async () => {
        const id = this.driftsMeddelelserService.selectedDriftsMeddelelse()?.id;
        if (id) {
          await this.driftsMeddelelserApi.remove(id);
        }
        this.driftsMeddelelserService.datasource.reloadDatasource();
        this.driftsMeddelelserService.activeTab.set('list');
      },
    });
  }

  async saveChanges() {
    if (this.form.invalid) {
      return;
    }

    this.saving.set(true);

    const driftsmeddelelse =
      this.driftsMeddelelserService.selectedDriftsMeddelelse();

    const newData = {
      ...driftsmeddelelse,
      ...this.form.value,
      produkter: this.form.value.produkter?.join(','),
      driftsystem: this.form.value.driftsystem?.join(','),
    } as DriftsMeddelelse;

    const systemKommentar = getDiffAsText(driftsmeddelelse || {}, newData);

    if (driftsmeddelelse?.id) {
      await this.driftsMeddelelseHistorikService.datasource.create({
        meddelelseId: driftsmeddelelse.id,
        kommentar: this.form.value.kommentar!,
        systemKommentar: systemKommentar,
      });

      await this.driftsMeddelelserApi.update(driftsmeddelelse.id, newData);
    } else {
      await this.driftsMeddelelserApi.create(newData);
    }

    this.saving.set(false);
    this.driftsMeddelelserService.datasource.reloadDatasource();
    this.driftsMeddelelserService.activeTab.set('list');
  }

  vistStatusOptions = [
    { value: 0, text: 'Blokerende hændelse' },
    { value: 1, text: 'Planlagt hændelse' },
    { value: 2, text: 'Information' },
  ];

  redigeringsStatusOptions = [
    { value: 0, text: 'Kladde' },
    { value: 1, text: 'Frigivet' },
    { value: 2, text: 'Afsluttet' },
  ];

  itcOptions = ['Alle', 'BD', 'BEC', 'SDC'];

  systemOptions = [
    { value: 'NemId', text: 'MitId loginside' },
    { value: 'Letportal', text: 'Letportal forside' },
    { value: 'Helbred', text: 'Helbred på Letportal forside' },
  ];
}
